import React, { forwardRef, ReactElement } from 'react' // Commons imports.
import Icon, { IconName } from '../Icon' // Sub imports.
import { IconButtonType } from './Constants' // CSS imports. Commented out because of current build process.
import './iconButton.scss'
import { IconSize } from '../../../../NonReact/constants/React/Icon/Constants'

/**
 * Component properties type.
 */
type Props<T = void> = {
    iconName: IconName
    onClick?: (event?: React.MouseEvent) => T | Promise<T>
    title?: string
    disabledSubTitle?: string
    customClassName?: string
    type: IconButtonType
    href?: string
    downloadable?: boolean
    target?: string
    isEnabled?: boolean
    onMouseOver?: (e?: React.MouseEvent) => void
    onMouseOut?: (e?: React.MouseEvent) => void
    buttonType?: 'button' | 'submit' | 'reset' | undefined
    iconSize?: IconSize
}

/**
 * Icon component.
 *
 * @param iconName Name of the icon compatible with eIconName enum values.
 * @param onClick Click callback handler.
 * @param title Title of the button.
 * @param disabledSubTitle Sub title to be added if button is disabled.
 * @param customClassName Custom class name.
 * @param type The type of the button.
 * @param href href link.
 * @param target Target link.
 * @param isEnabled Whether the button is enabled.
 * @param buttonType type definition of button.
 */
const IconButton = forwardRef<HTMLElement, Props<unknown>>(
    <T,>(
        {
            iconName,
            onClick,
            title,
            customClassName,
            type,
            href,
            downloadable,
            target,
            isEnabled,
            disabledSubTitle,
            onMouseOver,
            onMouseOut,
            buttonType,
            iconSize
        }: Props<T>,
        // eslint-disable-next-line
        ref: any
    ) => {
        let icon: ReactElement
        const className = customClassName ? `icon-button ${customClassName}` : 'icon-button'
        // Common properties for all button types.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const commonIconButtonProps = {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            ref,
            className,
            title: !isEnabled && disabledSubTitle ? decodeURI(`${title}%0A${disabledSubTitle}`) : title,
            onMouseOver,
            onMouseOut
        }

        switch (type) {
            case IconButtonType.LINK:
                icon = (
                    <a {...commonIconButtonProps} href={href} target={target} download={downloadable}>
                        {<Icon iconSize={iconSize ?? IconSize.MEDIUM} iconName={iconName} />}
                    </a>
                )
                break
            case IconButtonType.BUTTON:
            default:
                icon = (
                    <button
                        {...commonIconButtonProps}
                        type={buttonType ?? 'button'}
                        onClick={onClick}
                        disabled={typeof isEnabled === 'undefined' ? false : !isEnabled}
                        tabIndex={-1}
                    >
                        <Icon iconSize={iconSize ?? IconSize.MEDIUM} iconName={iconName} />
                    </button>
                )
                break
        }

        return <>{icon}</>
    }
) as <T>(props: Props<T> & { ref?: React.Ref<HTMLElement> }) => ReactElement
export default IconButton
